import { useDrawer } from "@consumidor-positivo/aurora";
import useAuthSession from "@libs/acp/hooks/useAuthSession";
import { useRedline } from "@libs/redline";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { pageNames } from "@shared/routes/pageNames";
import { useApiScore } from "@tally/apis/score/useApiScore";
import type { Notification } from "@tally/types/score";
import { usePrivatePagesStates } from "./data";

export const useLoggedInHeader = () => {
	const navigate = useNavigate();
	const { logout } = useAuthSession();
	const { track: trackRedline } = useRedline();

	const [notifications, setNotifications] = useState<Notification[]>([]);
	const { notificationRequest, notificationMarkAsRead, notificationDelete } =
		useApiScore();

	const { drawerOpen, handleOpenDrawer } = useDrawer({
		menu: false,
		notification: false,
	});

	const { dataSourceNavbar, dataSourceNavbarVertical } =
		usePrivatePagesStates();

	useEffect(() => {
		getNotifications();
	}, []);

	useEffect(() => {
		if (!drawerOpen.notification) return;
		trackRedline.userTracking.elementViewed({
			location: window.location.pathname,
			elementType: "content",
			name: "notifications",
			text: "visualizou as notificações",
			data: notifications,
		});
	}, [drawerOpen.notification]);

	async function getNotifications() {
		try {
			const { data } = await notificationRequest.send();
			setNotifications(data || []);
		} catch {
			// fail silently — notifications não são críticas
		}
	}

	async function markAsReadNotification(notification: Notification) {
		if (notification.read) return;
		try {
			await notificationMarkAsRead.send({ uuid: notification.uuid });
			setNotifications((prev) =>
				prev.map((n) =>
					n.uuid === notification.uuid ? { ...n, read: true } : n,
				),
			);
		} catch {
			// fail silently
		}
	}

	async function deleteNotification(notification: Notification) {
		trackRedline.userTracking.elementClicked({
			location: window.location.pathname,
			name: notification.title,
			uuid: notification.uuid,
			category: notification.category,
			text: "excluiu a notificação",
			path: notification.path || "",
		});
		try {
			await notificationDelete.send({ uuid: notification.uuid });
			setNotifications((prev) =>
				prev.filter((n) => n.uuid !== notification.uuid),
			);
		} catch {
			// fail silently
		}
	}

	async function handleNavigateNotification(notification: Notification) {
		trackRedline.userTracking.elementClicked({
			location: window.location.pathname,
			name: notification.title,
			uuid: notification.uuid,
			category: notification.category,
			text: "clicou na notificação",
			path: notification.path || "",
		});

		await markAsReadNotification(notification);

		const redirectMap: Record<string, string> = {
			score: pageNames.myScore.path,
			debts: pageNames.myDebts.path,
			offers: pageNames.offers.path,
			lookout: pageNames.cpfLookout.path,
			"negative-debts": pageNames.negativeDebts.path,
		};

		if (notification.category === "generic") {
			if (notification.path) window.open(notification.path, "_blank");
			return;
		}

		const path = redirectMap[notification.category];
		if (path) navigate(path);
	}

	function handleNavigate(path: string, title: string) {
		trackRedline.userTracking.elementClicked({
			elementType: "button/text",
			name: path,
			text: title,
			location: "navbar",
		});

		if (
			path === pageNames.myDebts.path ||
			path === pageNames.myAgreements.path
		) {
			window.location.href = path;
			return;
		}

		navigate(path);
	}

	function handleSignOut() {
		logout();
	}

	function handleLogoClick(isHome: boolean) {
		// abaixo de 1024px o Header.Logo do aurora renderiza o mobile (chevron
		// de voltar quando fora da home) — só nesse caso o click é "voltar"
		const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
		if (!isDesktop && !isHome) {
			navigate(-1);
			return;
		}
		handleNavigate(pageNames.myDebts.path, "logo");
	}

	function formatDate(date: string) {
		const d = new Date(date);
		const day = d.toLocaleDateString("pt-BR", {
			day: "2-digit",
			timeZone: "UTC",
		});
		const month = d.toLocaleDateString("pt-BR", {
			month: "short",
			timeZone: "UTC",
		});
		return `${day}/${month}`;
	}

	function firstName(name: string) {
		return name.split(" ")[0];
	}

	const news = notifications.filter(
		(n) => !n.read,
	) as unknown as Notification[];
	const olds = notifications.filter((n) => n.read) as unknown as Notification[];

	return {
		dataSourceNavbar,
		dataSourceNavbarVertical,
		drawerOpen,
		notifications,
		news,
		olds,
		formatDate,
		firstName,
		handleNavigate,
		handleOpenDrawer,
		handleNavigateNotification,
		deleteNotification,
		handleLogoClick,
		handleSignOut,
	};
};
